/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { trackedAction } from '../../utilities/track-execution-time';

/**
 * PostCart Input Action
 */
export class PostCartInput implements Msdyn365.IActionInput {
    public cartInput: IPostCartData;
    constructor(cartInput: IPostCartData) {
        this.cartInput = cartInput;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `PostCart`;
    public getCacheObjectType = () => 'PostCart';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IPostCartData {
    SalesOrg: string;
    AdditionalInfo: string;
    DistributionChannel: string;
    Division: string;
    Incoterm1: string;
    Incoterm2: string;
    CustomerPo: string;
    OrderType: string;
    SoldToId: string | null;
    ShipToId: string | null;
    Items: IPostCartItems[];
    Step: number;
    PriceFetch: boolean;
}

export interface IPostCartItems {
    Quantity: string;
    CustomerAccountNumber: string;
    Number: number;
    ProductNumber: string;
    ProductName: string;
    CustomerProductNumber: string;
    RequestedDate?: Date;
    UnitOfMeasure: string;
    EndCustomerNumber: string;
    EndCustomerName: string;
    PackagingQty?: string;
    MinimumQty?: string;
    MinimumQtyUnit?: string;
    MinimumQtyOnOrderLevel?: boolean;
    PriceUnit?: string;
    PriceFactor?: string;
    PriceAmount?: string;
    PriceCurrency?: string;
    MissingParameters?: string;
    PriceMissing?: string;
    Incoterm2?: string;
    IncotermLocation?: string;
}

export interface IPostCartResponseData {
    '@odata.context': string;
    Cart: any;
    Success: boolean;
    Message: string;
    Exception: string;
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostCartInput({} as IPostCartData);
};

async function action(input: PostCartInput, ctx: Msdyn365.IActionContext): Promise<IPostCartResponseData | null> {
    ensureAuthenticated(ctx);
    const response = await Msdyn365.sendRequest<IPostCartResponseData>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/SaveCart`,
        'post',
        {
            cartInput: input.cartInput
        },
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    );

    if (response.status === 200 && response.data) {
        return { ...response.data };
    } else {
        return null;
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostCartResponseData>>((input, ctx) => trackedAction(action, input, ctx, 'SaveCart')),
    id: 'PostCart',
    input: createInput
});
